<script setup lang="ts">
import type { ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { computed, toRefs } from 'vue';
import { provideBlockInjection } from '#layers/block-engine/public';
import { getSingleRelationFieldInputBlockOptions, useDataModelDisplayNames } from '#layers/data-model/public';
import { injectFormBlockContext, provideRelationFormFieldContext, RelationFieldLabelMenu, useInputBlockDisabled, useRelationFieldChanges, useRelationFieldState } from '#layers/forms/public';

const props = withDefaults(defineProps<{
	relationField: ResolvedMonospaceSingleRelationField;
	label?: string;
	description?: string;
	name?: string;
	/** Overrides whether the form's write requires the relation. */
	required?: boolean;
	ejected?: boolean;
}>(), { required: undefined });

const { relationField } = toRefs(props);

const { getSingleRelationFieldDisplayName } = useDataModelDisplayNames();
const label = computed(() => props.label ?? (relationField.value ? getSingleRelationFieldDisplayName(relationField.value) : ''));
const description = computed(() => props.description ?? relationField.value?.meta?.description ?? undefined);
const name = computed(() => props.name ?? relationField.value?.apiName);

const formBlock = injectFormBlockContext(null);
const required = computed(() => props.required ?? formBlock?.writeContext.value.requiresRelation(props.relationField) ?? false);
const { hasChanges, revert } = useRelationFieldChanges({ formBlockContext: formBlock, relationField, name });

const { referencedItemKey, referencedItemReferenceKey, handleCreate, handleUndoCreate, handleConnect, handleUndoConnect, handleDisconnect, handleUndoDisconnect, handleDelete, handleUndoDelete, handleUpdate, fieldValue } = useRelationFieldState(() => props.relationField);
const inputBlockProps = computed(() => ({
	...getSingleRelationFieldInputBlockOptions(relationField.value),
	fieldId: relationField.value.id,
	referencedItemKey: referencedItemKey.value,
}));

const blockDisabled = useInputBlockDisabled(inputBlockProps);

const { disabled } = provideRelationFormFieldContext({
	field: relationField,
	referencedItemKey,
	referencedItemReferenceKey,
	fieldValue,
	disabled: blockDisabled,
	emitCreate: handleCreate,
	emitUndoCreate: handleUndoCreate,
	emitConnect: handleConnect,
	emitUndoConnect: handleUndoConnect,
	emitDisconnect: handleDisconnect,
	emitUndoDisconnect: handleUndoDisconnect,
	emitDelete: handleDelete,
	emitUndoDelete: handleUndoDelete,
	emitUpdate: handleUpdate,
});

provideBlockInjection({
	fieldId: () => relationField.value.id,
	referencedItemKey,
});
</script>

<template>
	<DFormField
		:name
		:label
		:description
		:required
		:dirty="hasChanges"
		:ui="{ labelWrapper: hasChanges ? 'pe-4' : undefined }"
	>
		<!-- Trigger renders OUTSIDE the <Label> element via label-trailing slot -->
		<template v-if="hasChanges" #label-trailing>
			<RelationFieldLabelMenu
				:label
				:disabled
				:dirty="hasChanges"
				@revert="revert"
			/>
		</template>
		<template v-if="!ejected && inputBlockProps">
			<BlockRender v-bind="inputBlockProps" />
		</template>
		<slot v-else />
	</DFormField>
</template>
